import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

@Component({
    selector: 'app-update-banner',
    templateUrl: './update-banner.component.html',
    styleUrls: ['./update-banner.component.scss'],
})
export class UpdateBannerComponent {
    /**
     * How the banner reads. `'warning'` is the original look and the default, so every existing
     * caller is unaffected: orange ground, exclamation icon.
     *
     * `'progress'` is for work that is running and needs no decision from the reader — no action
     * button belongs on it. `'success'` reports a finished result.
     */
    @Input() variant: 'warning' | 'progress' | 'success' = 'warning';

    @Input() messageLocalizationKey: string = undefined;
    @Input() buttonLocalizationKey: string = undefined;
    @Input() message: string = undefined;
    @Input() linkUrl: string = undefined;
    @Input() dismissible: boolean = false;
    @Input() disabled: boolean = false;
    @Input() disabledTooltip: string = undefined;

    @Output() confirm = new EventEmitter<void>();
    @Output() dismiss = new EventEmitter<void>();

    // –– bind the native `hidden` attribute on the host
    @HostBinding('attr.hidden') get hidden() {
        return !this.isVisible ? '' : null;
    }

    // The variant as a host class, so the stylesheet can override the (orange) defaults on `:host`
    // by specificity instead of every rule needing a modifier.
    @HostBinding('class.banner--progress') get isProgress(): boolean {
        return this.variant === 'progress';
    }

    @HostBinding('class.banner--success') get isSuccess(): boolean {
        return this.variant === 'success';
    }

    onConfirm() {
        this.confirm.emit();
    }

    onDismiss() {
        this.dismiss.emit();
    }

    get isVisible(): boolean {
        return this.messageLocalizationKey !== undefined || this.message !== undefined;
    }

    get hasButton(): boolean {
        return this.buttonLocalizationKey !== undefined;
    }

    get hasLink(): boolean {
        return this.linkUrl !== undefined && this.linkUrl.trim() !== '';
    }

    get displayMessage(): string {
        return this.message || '';
    }
}
